<template>
    <div class="algolia-search-bar" v-click-outside="onHide">
        <search-bar
            ref="searchInput"
            @type="onTypeThrottle"
            @search="onSearch"
            @blur="onBlur"
            @down="onDownInitial"
            :query="query"
            :suggestions="products"
            :showSuggestions="showSuggestionsInternal">
            <template v-slot:suggestions>
                <div class="suggestions-split-container">
                    <div v-if="suggestions && suggestions.length" class="suggestions">
                        <h6>Suggestions</h6>
                        <div v-for="(suggestion, index) in suggestions" :key="suggestion.objectID" class="search-bar-suggestion">
                            <a
                                tabindex="0"
                                @keydown.enter="onSearch(suggestion.query)"
                                @keydown.up.prevent="onUp(index)"
                                @keydown.down.prevent="onDown(index)"
                                @keydown.right.prevent="onRight(index)"
                                @keydown.esc="onBlur"
                                @click="onSearch(suggestion.query)"
                                class="search-bar-suggestion-link"
                                ref="searchSuggestions"
                            >
                                <span v-for="(part, index) in formatSuggestion(suggestion)" :key="index" :class="{ bold: index % 2 }">{{ part }}</span>
                            </a>
                        </div>
                    </div>

                    <div v-if="products && products.length" class="suggestions-right-column">
                        <hr v-if="suggestions && suggestions.length" class="suggestions-mobile-divider" />
                        <div class="suggestions-product-heading"><h6><template>{{productsCount}} </template>Product<template v-if="!productsCount || productsCount > 1">s </template></h6> <template v-if="productsCount || productsCount > 3"> <span class="dash"> — </span> <a @click.prevent="onSearch(query)" class="suggestions-show-all">  Show All</a></template></div>

                        <div v-for="(suggestion, index) in products" :key="suggestion.id">
                            <a
                                tabindex="0"
                                @keydown.up.prevent="onUp(suggestions.length + index)"
                                @keydown.down.prevent="onDown(suggestions.length + index)"
                                @keydown.left.prevent="onLeft(index)"
                                @keydown.esc="onBlur"
                                :href="suggestion.url"
                                class="suggestion-link"
                                ref="searchProductSuggestions"
                                @click="clickProduct(suggestion, index)"
                            >
                                <div class="suggestion-image">
                                    <responsive-image :lazy="false" :image="suggestion.productImage" :widths="[130]" />
                                </div>
                                <div>
                                    <div class="suggestion-title">{{ suggestion.title }}</div>
                                    <div>
                                        <span class="suggestion-variation" v-if="suggestion.swatchImage && suggestion.variations && suggestion.variations.length">
                                            <responsive-image :lazy="false" :image="suggestion.swatchImage" :widths="[12]" />
                                        </span><span class="suggestion-variation" v-for="variation in suggestion.variations" :key="variation.id">
                                            <responsive-image :lazy="false" :image="variation.swatchImage" :widths="[12]" />
                                        </span>
                                    </div>
                                    <div class="suggestion-price bold accent">{{ suggestion.price }}</div>
                                </div>
                            </a>
                        </div>
                    </div>

                    <div v-if="helpResults && helpResults.length" class="suggestions-right-column help-results">
                        <hr v-if="suggestions && suggestions.length" class="suggestions-mobile-divider" />
                        <div class="suggestions-product-heading"><h6><template>{{helpCenterCount}} </template>Help Center</h6> <template v-if="helpCenterCount || helpCenterCount > 3"> <span class="dash"> — </span> <a @click.prevent="seeAllHelpCenterResults()" class="suggestions-show-all">  Show All </a></template></div>
                        <div v-for="(suggestion, index) in helpResults" :key="suggestion.objectID">
                            <a
                                tabindex="0"
                                @keydown.up.prevent="onUp(suggestions.length + index)"
                                @keydown.down.prevent="onDown(suggestions.length + index)"
                                @keydown.left.prevent="onLeft(index)"
                                @keydown.esc="onBlur"
                                @click="goToHelpPage(suggestion.slug)"
                            >

                            <div class="help-center-title">{{suggestion.title}}</div>
                            <div class="help-center-content" v-html="suggestion._snippetResult.htmlContent.value"></div>
                            </a>
                        </div>
                    </div>

                    <div v-if="(!suggestions || !suggestions.length) && (!products || !products.length) && (!helpResults || !helpResults.length) && !loading && query">
                        No results for <span class="accent">‘{{ query }}’</span>
                    </div>
                </div>
            </template>
        </search-bar>
    </div>
</template>
<script>
import SearchBar from './SearchBar.vue';
import { loadSuggestions, loadHelpCenterResults } from '@/components/browse/ProductSearchService';
import { Image } from '@article/pixel';
import ClickOutside from 'vue-click-outside'
import { throttle } from 'lodash-es';
import Tracking from '@/components/commons/Tracking';
import { queueProductClick } from '@/components/commons/AlgoliaAnalytics';
import LocationService from '@/services/LocationService';

export default {
    components: {
        'ResponsiveImage': Image,
        SearchBar
    },

    props: ['show', 'initialQuery', 'enableSuggestions',],

    data() {
        return {
            products: [],
            helpResults: [],
            suggestions: [],
            productsCount: 0,
            showSuggestions: false,
            query: this.initialQuery,
            loading: 1,
            focusSuggestion: null,
            productsQueryId: null,
            productsQueryIndex: null
        }
    },

    created() {
        this.queryCount = 1;
        this.onTypeThrottle = throttle(query => this.onType(query), 300);
    },

    methods: {

        onHide() {
            this.showSuggestions = false;
        },

        onSearch(query) {
            this.query = query;
            this.showSuggestions = false;
            if(this.helpResults.length > 0 && this.productsCount === 0){
                this.seeAllHelpCenterResults()
            }else{
                this.$emit('search', query);
                this.$emit('loadHelpCenter', query);
            }
            this.$refs.searchInput.focus();
        },

        async onType(query) {
            this.query = query;

            if(!query) {
                this.showSuggestions = false;
                this.$emit('type', query);
                return;
            }

            this.showSuggestions = true;
            
            const preQueryCount = this.queryCount++;
            this.loading = preQueryCount;
            const currencyId = LocationService.getCurrencyId();

            const {
                products,
                suggestions,
                productsCount,
                productsQueryId,
                productsQueryIndex
            } = await loadSuggestions(query, currencyId, { analytics: true });
            
            if(query.length > 2){
                let helpCenterResults = await loadHelpCenterResults(query);
                this.helpResults = helpCenterResults.results.hits.filter((x) => { if(x._rankingInfo.words > 0 && x._rankingInfo.nbTypos !== 1){ return x } });
            }else{
                this.helpResults = []
            }

            if(this.loading === preQueryCount) {
                this.loading = null;
            }

            this.suggestions = suggestions;
            this.products = products;
            this.productsCount = productsCount;
            this.productsQueryId = productsQueryId;
            this.productsQueryIndex = productsQueryIndex;

            this.$emit('type', query);

            try {
                if(this.showSuggestionsInternal) {
                    Tracking.track('Suggestions Viewed', {
                        query: query,
                        suggestions: suggestions.map(suggestion => suggestion.query).join(','),
                        products: products.map(product => product.id).join(',')
                    });
                }
            } catch(e) {
                console.warn('Unable to track search suggestion');
                console.warn(e);
            }
        },

        onBlur() {
            this.showSuggestions = false;
        },

        focus() {
            this.$refs.searchInput.focus();
        },

        onDownInitial() {
            if(!this.showSuggestions) {
                this.onType(this.query);
            }

            const suggestions = this.getSuggestionElements();
            const suggestionElement = suggestions && suggestions.length ? suggestions[0] : null;

            if(suggestionElement) {
                suggestionElement.focus();
            }
        },

        onUp(index) {
            if(!index) {
                this.$refs.searchInput.focus();
            }

            const suggestions = this.getSuggestionElements();
            const suggestionElement = suggestions && suggestions.length ? suggestions[index - 1] : null;
            
            if(suggestionElement) {
                suggestionElement.focus();
            }
        },
        
        onDown(index) {
            const suggestions = this.getSuggestionElements();
            const suggestionElement = suggestions && suggestions.length > index ? suggestions[index + 1] : null;
            
            if(suggestionElement) {
                suggestionElement.focus();
            }
        },

        onLeft(index) {
            const suggestions = this.getSuggestionElements();
            const suggestionElement = suggestions && suggestions.length && this.suggestions && this.suggestions.length ? suggestions[0] : null;

            if(suggestionElement) {
                suggestionElement.focus();
            }
        },

        onRight(index) {
            const suggestions = this.getSuggestionElements();
            const suggestionElement = suggestions && suggestions.length && this.suggestions && this.suggestions.length && this.products && this.products.length ? suggestions[this.suggestions.length] : null;

            if(suggestionElement) {
                suggestionElement.focus();
            }
        },

        getSuggestionElements() {
            return [...(this.$refs.searchSuggestions || []), ...(this.$refs.searchProductSuggestions || [])];
        },

        goToHelpPage(slug){
            window.location = `/help-center/${slug}`
        },

        seeAllHelpCenterResults(){
            window.location = `/help-center/search?query=${this.query}`
        },

        formatSuggestion(suggestion) {
            const term = suggestion._highlightResult.query.value;
            let parts = term.split(/<em>|<\/em>/);

            if(parts.length && parts[0].length) {
                parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
            } else if(parts.length > 1 && parts[1].length) {
                parts[1] = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
            }

            return parts;
        },

        clickProduct(product, position) {
            queueProductClick(this.productsQueryIndex, product.id, this.productsQueryId, position);
        }
    },

    computed: {
        helpCenterCount(){
            return this.helpResults.length
        },

        showSuggestionsInternal() {
            return this.enableSuggestions && this.showSuggestions;
        },
    },

    watch: {
        showSuggestions: {
            handler(val) {
                if(this.enableSuggestions) {
                    if(val) {
                        this.$emit('showSuggestions');
                    } else {
                        this.$emit('hideSuggestions');
                    }
                }
            }
        }
    },
        
    directives: {
        ClickOutside
    }
}
</script>
<style lang="scss">
@import "~@article/pixel/dist/style/variables";

.algolia-search-bar {
    .suggestions-split-container {
        display: flex;

        .suggestions{
            width: 15%;
            padding-right: var(--space-10);
        }

        .suggestions-right-column { 
            width: 30%;
            padding-right: var(--space-10);
        }

        .suggestions, .suggestions-right-column { 
            h6{
                padding-right: var(--space-1);
            }
        }

        .suggestions h6, .suggestions-product-heading{
            padding-bottom: var(--space-4); 
            margin-bottom: var(--space-4); 
            border-bottom: 1px solid var(--grey-light);
        }

        @media (max-width: $mobile-breakpoint - 1) {
            display: block;
            .suggestions, .suggestions-right-column{
                width: 100%;
                padding-right: 0;
            }
        }
    }

    .dash{
        color: var(--grey-light);
    }

    .suggestions-mobile-divider {
        display: none;
        margin-top: 10px;
        margin-bottom: 10px;

        @media (max-width: $mobile-breakpoint - 1) {
            display: block;
        }
    }

    .suggestions-show-all{ 
        font-size: var(--font-size-14);
        line-height: var(--font-size-14);
        color: var(--coral);
        text-decoration: underline;
        margin-left: var(--space-1);
    }

    .suggestion-link {
        display: flex;
        align-items: center;
        margin-bottom: 0;
        padding: var(--space-2) 0;

        &:focus {
            color: var(--grey-medium);
        }
    }

    .suggestion-image {
        width: 130px;
        min-width: 130px;
        margin-right: 15px;
    }

    .suggestion-variation {
        margin-right: 4px;
        display: inline-block;
        border-radius: 50%;
        overflow: hidden;
        width: 12px;
        height: 12px;
    }

    .suggestions-product-heading {
        display: flex;
        align-items: baseline;
    }

    .suggestion-title, .suggestion-price {
        font-size: var(--font-size-14);
        line-height: var(--line-height-14);
    }

    .search-bar-suggestion-link {
        display: block;
        margin-right: 30px;

        &:focus {
            color: var(--grey-medium);
        }
    }


    .help-results{
        a{
            display: block;
            div{
                display: block;
                width: 100%;

                &.help-center-title{
                    color: var(--coral);
                    text-transform: capitalize;
                    text-decoration-color: var(--coral);
                    text-decoration: underline;
                }

                &.help-center-content{
                    position: relative;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    font-size: var(--font-size-14);
                    padding-bottom: var(--space-4);
                }

                em{
                    display: inline-block;
                    font-weight: bold;
                    font-style: normal;
                }
            }

        }
    }

}
</style>
